import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, AlertTriangle, Building2 } from 'lucide-react'
import { listFirmenOhneAdmin } from '@/api/kostenuebernahme'
import Spinner from '@/components/Spinner'
import ErrorAlert from '@/components/ErrorAlert'

/**
 * Phase I — Superadmin-Inbox "Firmen ohne Admin".
 *
 * Zeigt alle Kostenübernahmen, deren Firma im CRM gematched ist (CrmAccountId
 * gesetzt) aber KEINEN Portal-Admin hat. Spec §8 — Case 3 Eskalation.
 *
 * Klick auf Zeile öffnet die bestehende KÜ-Detail-Page, von dort kann der
 * Superadmin manuell:
 *   - einen User zum Admin promoten
 *   - selbst Permissions vergeben
 *   - ignorieren (AP arbeitet weiter ohne Firma-Admin)
 */
export default function FirmenOhneAdminPage() {
  const navigate = useNavigate()

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['firmen-ohne-admin'],
    queryFn: () => listFirmenOhneAdmin({ skip: 0, limit: 200 }),
  })

  const items = data?.items ?? []

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-lg px-6 py-5 mb-5">
        <button
          onClick={() => navigate('/superadmin/kostenuebernahmen')}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-2"
        >
          <ArrowLeft size={14} />
          Zurück zur Übersicht
        </button>
        <h1 className="text-[1.35rem] font-bold text-gray-900 tracking-tight flex items-center gap-2">
          <Building2 size={20} className="text-fom-500" />
          Firmen ohne Admin
        </h1>
        <p className="text-gray-500 text-[0.78rem] mt-0.5">
          Eskalations-Inbox für Case 3 (Spec §8): Firma matched, aber kein Portal-Admin.
        </p>
      </div>

      {/* Hinweis-Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-5 flex gap-3 items-start">
        <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <b>Diese Firmen haben aktive Zuzahler-Ansprechpartner, aber keinen Portal-Admin.</b>
          <p className="mt-1">
            Bitte einen Admin zuweisen oder Permissions manuell vergeben. Solange
            kein Admin existiert, kann ein neuer AP nicht automatisch bestätigt
            werden — der Vorgang läuft trotzdem (Case-3-Setup), aber niemand in
            der Firma sieht die Aufgabe im Portal.
          </p>
        </div>
      </div>

      {/* Content */}
      {isLoading && <Spinner className="mt-10" />}
      {error && <ErrorAlert message={(error as Error).message} onRetry={refetch} />}

      {!isLoading && !error && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="text-xs text-gray-400 px-4 py-2 border-b border-gray-100">
            {items.length} {items.length === 1 ? 'Eintrag' : 'Einträge'}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                <th className="px-4 py-3 text-left">Studierende/r</th>
                <th className="px-4 py-3 text-left">Zuzahler-Firma</th>
                <th className="px-4 py-3 text-left">AP</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Erstellt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => (
                <tr
                  key={item.Id}
                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/superadmin/kostenuebernahmen/${item.Id}`)}
                >
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {[item.StudiVorname, item.StudiNachname].filter(Boolean).join(' ') || '–'}
                    {item.StudiMatrikel && (
                      <div className="text-xs text-gray-400 font-mono mt-0.5">
                        {item.StudiMatrikel}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {item.ZuzahlerFirma ?? '–'}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    <div className="font-medium">{item.ZuzahlerApName ?? '–'}</div>
                    {item.ZuzahlerApEmail && (
                      <div className="text-xs text-gray-500 mt-0.5">{item.ZuzahlerApEmail}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {item.Status}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {new Date(item.CreatedAt).toLocaleDateString('de-DE')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && (
            <div className="text-center text-gray-400 py-12 text-sm">
              Keine Einträge — alle gematchten Firmen haben einen Portal-Admin.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
